// store/authSlice.ts (version corrigée)
import { User } from "@/types/models";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import CryptoJS from "crypto-js";

// Clé secrète pour le chiffrement
const SECRET_KEY = "votre_clé_secrète_pour_chiffrement";

export type AccountType = "personal" | "organization";

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken?: string | null;
  isAuthenticated?: boolean;
  tokenNotification?: string | null;
  pushEnabled?: boolean;
  organisation?: any | null;
  accountType: AccountType;
  activeOrganizationId: string | null;
}

function encryptData(data: string): string {
  return CryptoJS.AES.encrypt(data, SECRET_KEY).toString();
}

function decryptData(ciphertext: string): string {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8) || "";
  } catch (error) {
    console.error("Erreur lors du décryptage :", error);
    return "";
  }
}

const loadInitialState = (): AuthState => {
  try {
    if (typeof window === "undefined") return getDefaultAuthState();

    const storedUser = localStorage.getItem("auth-user");
    const storedTokens = localStorage.getItem("auth-tokens");
    const storedPushEnabled: any = localStorage.getItem("notifications");
    const storedNotificationToken = localStorage.getItem("notifications-ssid");
    const storedAccountType = localStorage.getItem("account-type");
    const storedActiveOrganizationId = localStorage.getItem("active-organization-id");

    const user = storedUser ? JSON.parse(decryptData(storedUser)) : null;
    const tokens = storedTokens ? JSON.parse(decryptData(storedTokens)) : null;

    const pushEnabled = storedPushEnabled ? JSON.parse(storedPushEnabled) : true;
    const tokenNotification = storedNotificationToken || null;
    
    // IMPORTANT: Récupérer le type de compte depuis localStorage
    let accountType: AccountType = "personal";
    if (storedAccountType === "organization") {
      accountType = "organization";
    } else if (storedAccountType === "personal") {
      accountType = "personal";
    }
    
    const activeOrganizationId = storedActiveOrganizationId || null;

    // Si on est en mode organisation et qu'on a un ID, vérifier que l'utilisateur a bien cette organisation
    let finalAccountType = accountType;
    let finalActiveOrgId = activeOrganizationId;
    
    if (accountType === "organization" && activeOrganizationId && user?.organizations) {
      const hasOrg = user.organizations.some((org: any) => org.id === activeOrganizationId);
      if (!hasOrg) {
        finalAccountType = "personal";
        finalActiveOrgId = null;
      }
    }

    return {
      user: user ?? null,
      accessToken: tokens?.accessToken ?? null,
      refreshToken: tokens?.refreshToken ?? null,
      isAuthenticated: !!tokens?.accessToken,
      pushEnabled,
      tokenNotification,
      organisation: user?.organizations?.[0] || user?.affectation?.[0] || null,
      accountType: finalAccountType,
      activeOrganizationId: finalActiveOrgId,
    };
  } catch (error) {
    console.error("Erreur lors du chargement :", error);
    return getDefaultAuthState();
  }
};

const getDefaultAuthState = (): AuthState => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  pushEnabled: true,
  tokenNotification: null,
  organisation: null,
  accountType: "personal",
  activeOrganizationId: null,
});

const initialState: AuthState = loadInitialState();

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    handleStoreUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
      state.organisation = action.payload?.organizations?.[0] || action.payload?.affectation?.[0] || null;
      
      // IMPORTANT: Ne pas perdre le type de compte et l'organisation active
      // Si l'utilisateur a une organisation active dans le state, on la conserve
      if (typeof window !== "undefined") {
        const encryptedUser = encryptData(JSON.stringify(action.payload));
        localStorage.setItem("auth-user", encryptedUser);
      }
    },

    saveUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.organisation = action.payload?.organizations?.[0] || action.payload?.affectation?.[0] || null;
      
      const encryptedUser = encryptData(JSON.stringify(action.payload));
      if (typeof window !== "undefined") {
        localStorage.setItem("auth-user", encryptedUser);
      }
    },

    handleStoreOrganisation: (state, action: PayloadAction<any>) => {
      state.organisation = action.payload;
      if (typeof window !== "undefined") {
        localStorage.setItem("organisation", encryptData(JSON.stringify(action.payload)));
      }
    },

    handleStoreToken: (state, action: PayloadAction<{
      accessToken?: string;
      refreshToken?: string;
      tokenNotification?: string;
    }>) => {
      if (action.payload.accessToken) state.accessToken = action.payload.accessToken;
      if (action.payload.refreshToken) state.refreshToken = action.payload.refreshToken;
      if (action.payload.tokenNotification) state.tokenNotification = action.payload.tokenNotification;
      state.isAuthenticated = Boolean(action.payload.accessToken);

      if (typeof window !== "undefined" && action.payload.accessToken) {
        const encryptedTokens = encryptData(JSON.stringify({
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        }));
        localStorage.setItem("auth-tokens", encryptedTokens);
      }
    },

    saveToken: (state, action: PayloadAction<{ accessToken: string; refreshToken?: string }>) => {
      const { accessToken, refreshToken } = action.payload;
      const encryptedTokens = encryptData(JSON.stringify({ accessToken, refreshToken }));
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = Boolean(accessToken);
      if (typeof window !== "undefined") {
        localStorage.setItem("auth-tokens", encryptedTokens);
      }
    },

    handleStoreNotificationToken: (state, action: PayloadAction<{ tokenNotification?: string }>) => {
      state.tokenNotification = action.payload.tokenNotification;
      if (typeof window !== "undefined" && action.payload.tokenNotification) {
        localStorage.setItem("notifications-ssid", action.payload.tokenNotification);
      }
    },

    handleStorePush: (state, action: PayloadAction<{ pushEnabled: boolean }>) => {
      state.pushEnabled = action.payload.pushEnabled;
      if (typeof window !== "undefined") {
        localStorage.setItem("notifications", JSON.stringify(action.payload.pushEnabled));
      }
    },

    saveNotifications: (state, action: PayloadAction<{
      tokenNotification?: string | any;
      pushEnabled?: boolean | any;
    }>) => {
      const { tokenNotification, pushEnabled } = action.payload;

      if (typeof pushEnabled !== "undefined") state.pushEnabled = pushEnabled;
      if (typeof tokenNotification !== "undefined") state.tokenNotification = tokenNotification;

      if (typeof window !== "undefined") {
        if (typeof pushEnabled !== "undefined") {
          localStorage.setItem("notifications", JSON.stringify(pushEnabled));
        }
        if (typeof tokenNotification !== "undefined") {
          localStorage.setItem("notifications-ssid", tokenNotification);
        }
      }
    },

    setActiveOrganization: (state, action: PayloadAction<string | null>) => {
      state.activeOrganizationId = action.payload;
      if (action.payload) {
        state.accountType = "organization";
      }
      if (typeof window !== "undefined") {
        if (action.payload) {
          localStorage.setItem("active-organization-id", action.payload);
        } else {
          localStorage.removeItem("active-organization-id");
        }
        localStorage.setItem("account-type", state.accountType);
      }
    },

    setAccountType: (state, action: PayloadAction<AccountType>) => {
      state.accountType = action.payload;
      if (action.payload === "personal") {
        state.activeOrganizationId = null;
        if (typeof window !== "undefined") {
          localStorage.removeItem("active-organization-id");
        }
      }
      if (typeof window !== "undefined") {
        localStorage.setItem("account-type", action.payload);
      }
    },

    // Nouveau reducer pour restaurer l'état après rafraîchissement
    restoreAccountState: (state) => {
      if (typeof window !== "undefined") {
        const storedAccountType = localStorage.getItem("account-type");
        const storedActiveOrgId = localStorage.getItem("active-organization-id");
        
        if (storedAccountType === "organization" && storedActiveOrgId && state.user?.organizations) {
          const hasOrg = state.user.organizations.some((org: any) => org.id === storedActiveOrgId);
          if (hasOrg) {
            state.accountType = "organization";
            state.activeOrganizationId = storedActiveOrgId;
          } else {
            state.accountType = "personal";
            state.activeOrganizationId = null;
          }
        } else if (storedAccountType === "personal") {
          state.accountType = "personal";
          state.activeOrganizationId = null;
        }
      }
    },

    handleLogout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.organisation = null;
      state.isAuthenticated = false;
      state.accountType = "personal";
      state.activeOrganizationId = null;
      state.tokenNotification = null;
      state.pushEnabled = true;

      if (typeof window !== "undefined") {
        localStorage.removeItem("auth-user");
        localStorage.removeItem("auth-tokens");
        localStorage.removeItem("organisation");
        localStorage.removeItem("active-organization-id");
        localStorage.removeItem("account-type");
      }
    },

    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.organisation = null;
      state.accountType = "personal";
      state.activeOrganizationId = null;

      if (typeof window !== "undefined") {
        localStorage.removeItem("auth-user");
        localStorage.removeItem("auth-tokens");
        localStorage.removeItem("organisation");
        localStorage.removeItem("active-organization-id");
        localStorage.removeItem("account-type");
      }
    },
  },
});

export const {
  handleStoreUser,
  handleStoreOrganisation,
  handleStoreToken,
  handleStorePush,
  handleStoreNotificationToken,
  setActiveOrganization,
  setAccountType,
  handleLogout,
  saveUser,
  saveToken,
  saveNotifications,
  logout,
  restoreAccountState, // Exporter le nouveau reducer
} = authSlice.actions;

export default authSlice.reducer;